/*
 * A notification menu for PubSub notifications.
 */
import './styles/Notifications.scss';
import { Fragment as F } from 'react';
import CoreComponent from './CoreComponent';
import TimedBar from './TimedBar';

class ToastItem extends CoreComponent {
    constructor(props) {
        super(props);
        this.icons = {
            info: 'circle-info-fill',
            success: 'circle-check-fill',
            error: 'triangle-alert-fill',
            warning: 'circle-alert-fill',
            default: 'star-fill',
            question: 'circle-question-fill',
            loading: 'spinner-circle-dots pulse',
        };
    }

    static shallowCompare(a, b) {
        return Object.keys(a).concat(Object.keys(b)).every(k => a[k] === b[k])
    }

    static getDerivedStateFromProps(props = {}, state = {}) {
        return !ToastItem.shallowCompare(props, state) ? { ...state, ...props } : null;
    }

    remove(toast, cb = this.props.onRemove) {
        this.setStatePrev({
            close: true,
        }).then(() => {
            this.setTimeout(() => {
                this.setStatePrev({
                    removed: true,
                }).then(() => (cb || (() => { }))(toast))
            }, 700);
        });
    }

    render() {
        let { toast, closeOnClick, onRemove, ...other } = this.props,
            buttons = (toast.buttons || []).length >= 1 ? toast.buttons : false;

        return (
            <F>
                {
                    this.state.removed ? null :
                        <div className={`NotificationToast${toast.onClick ? ' has-click-toast' : ''} ${toast.type || 'default'}-toast ${this.state.close ? 'closing-toast' : ''}`} onClick={
                            (event) => {
                                if (closeOnClick || toast.closeOnClick) this.remove(toast, onRemove);
                                (toast.onClick || (() => { })).call(event, toast);
                            }
                        } {...other}>
                            <i className={`cf-icon-${toast.icon || this.icons[toast.type] || this.icons['default']}`} style={toast.iconStyle || {}} />
                            <div
                                className="message-toast"
                                dangerouslySetInnerHTML={
                                    {
                                        __html: NotificationFilter.sanitize(toast.text || toast.message || 'You have a new notification!'),
                                    }
                                }
                            />
                            {
                                !toast.noClose && <span className="cf-icon-exit close-toast" onClick={
                                    (evnt) => {
                                        evnt.stopPropagation();
                                        this.remove(toast, onRemove);
                                    }
                                } />
                            }
                            {
                                buttons && <span className="buttons-toast">
                                    {
                                        [...buttons].map((btn, idx) => {
                                            return (
                                                <span key={`${idx}.btn`} className={`btn-toast ${btn.type || 'default'}-toast-btn`} style={btn.style || {}} onClick={
                                                    (event) => {
                                                        (btn.onClick || (() => { })).call(this, event, () => {
                                                            this.remove(toast, onRemove);
                                                        });
                                                    }
                                                }>
                                                    {btn.text}
                                                </span>
                                            )
                                        })
                                    }
                                </span>
                            }
                            {
                                toast.timeout && <TimedBar
                                    time={toast.timeout}
                                    type={toast.type}
                                    style={{ position: 'absolute', bottom: 0, height: '2px' }}
                                    finished={() => this.remove.call(this, this.props.toast)}
                                />
                            }
                        </div>
                }
            </F>
        )
    }
}

class NotificationToasts extends CoreComponent {
    constructor(props) {
        super(props);
        this.positions = [
            'under-left', 'center',
            'top-left', 'top-center', 'top-right',
            'bottom-left', 'bottom-center', 'bottom-right',
        ];
    }

    sortToasts(toasts = [], _default = 'top-center', blockers = []) {
        let results = this.positions.reduce((accumulator, current) => {
            return (accumulator[current] = []) && accumulator;
        }, {
            blockers: ((blockers = toasts.filter((toast) => toast.blocking)).length >= 1 ? blockers : false),
        });

        (results.blockers || toasts).forEach((toast) => {
            results[this.positions.includes(toast.position) ? toast.position : _default].push(toast);
        });

        return results;
    }

    render() {
        let { toasts, position, toastRemoved, onRemove, ...other } = this.props,
            _toasts = this.sortToasts(toasts, position);

        return (
            <div className={`NotificationToastsWrapper ${_toasts.blockers ? 'enabled' : 'disabled'}`}>
                {
                    this.positions.map((_position) => {
                        if (!_toasts[_position].length) return null;
                        return (
                            <div key={_position} className={`NotificationToasts NotificationPosition N-${_position}`}>
                                {
                                    [..._toasts[_position]][/^bottom-/.test(_position) ? 'reverse' : 'valueOf']().map((toast) => {
                                        return React.cloneElement(<ToastItem key={`toast-${toast.__KEY || Math.random()}`} onRemove={onRemove || toastRemoved} />, Object.assign({ toast: toast }, other));
                                    })
                                }
                            </div>
                        );
                    })
                }
            </div>
        );
    }
}

class ToastObject {
    constructor(props, __KEY = `${Date.now().toString(36)}+${(Math.random() * 50e6).toString(36)}`) {
        Object.assign(this, props);
        Object.defineProperty(this, '__KEY', {
            enumerable: false,
            writable: false,
            value: __KEY,
        });
    }
}
class Toast extends ToastObject { }

/*
 * Filter out duplicate notifications by key, keep the newest available.
 * This class is used to manage old style notificcations using Arrays,
 * without the use of the NotificationStore class.
 */
class NotificationFilter {
    constructor(...notifications) {
        this.notifications = this.flatten(notifications);
    }

    static flatten(array = []) {
        return array.reduce((a, b) => a.concat(Array.isArray(b) ? NotificationFilter.flatten(b) : b), []);
    }

    flatten(...args) {
        return this.constructor.flatten.call(this, args);
    }

    static unique(key = 'id', notifications = false) {
        return (notifications || this.notifications || []).reverse().filter((obj, pos, arr) => {
            return !obj[key] ? true : arr.map(mapObj => mapObj[key]).indexOf(obj[key]) === pos;
        }).reverse();
    }

    unique(...args) {
        return this.constructor.unique.call(this, args);
    }

    static sanitize(src, rgx = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script\s*>/gi) {
        while (rgx.test(src)) {
            src = src.replace(rgx, '');
        }
        return src;
    }

    sanitize(...args) {
        return this.constructor.sanitize.apply(this, args);
    }

    static remove(toasts = [], keys = []) {
        let _keys = (Array.isArray(keys) ? keys : [keys]).map((k) => k instanceof ToastObject ? k.__KEY : k);
        return [...toasts].filter((t) => !t.__KEY || !_keys.includes(t.__KEY));
    }

    remove(...args) {
        return this.constructor.remove.apply(this, args);
    }

    static update(toasts = [], key = null, update) {
        let _key = (key instanceof ToastObject ? key.__KEY : key),
            _toasts = [...toasts];
        if (_toasts.find((t) => t.__KEY == _key)) {
            for (let tid in _toasts) {
                if (_toasts[tid].__KEY === _key) {
                    _toasts[tid] = Object.assign(_toasts[tid], (Object.keys(update).length >= 1 ? update : key) || {});
                    break;
                }
            }
        } else {
            return _toasts.concat((key instanceof ToastObject) ? Object.assign(key, update) : new Toast(Object.assign(key, update), _key));
        }
        return _toasts;
    }

    update(...args) {
        return this.constructor.update.apply(this, args);
    }

    static combine(arrays, toast = false, id = 'id') {
        return NotificationFilter.unique((toast ? '__KEY' : id), NotificationFilter.flatten(arrays));
    }

    combine(...args) {
        return this.constructor.combine.call(this, args);
    }
}

class NotificationStore extends Map {
    constructor(props) {
        super(props);
    }

    static toKey(key = null, gen = true) {
        return (
            key && (key instanceof ToastObject || (key.constructor === Object && '__KEY' in key)) ?
                key.__KEY
                :
                !key ? (gen ? this.GenerateID() : false) : key
        );
    }

    toKey(...args) {
        return this.constructor.toKey.apply(this, args);
    }

    static toToast(value, key) {
        let _key;
        return [
            (_key = (
                value && (value instanceof ToastObject || (value.constructor === Object && '__KEY' in value)) ?
                    value.__KEY
                    :
                    this.toKey(key)
            )),
            value instanceof ToastObject ?
                {
                    ...(this.has(_key) ? this.get(_key) : {}),
                    ...value,
                    __KEY: _key,
                }
                : new Toast(value, _key),
        ];
    }

    toToast(...args) {
        return this.constructor.toToast.apply(this, args);
    }

    has(key = false) {
        return key ? Map.prototype.has.call(this, this.toKey(key, false)) : this.sizs >= 1;
    }

    set(key = undefined, value) {
        return value ?
            Promise.resolve(Map.prototype.set.apply(this, this.toToast(value, key)))
            :
            Promise.reject();
    }

    add(value) {
        return this.set.call(this, undefined, value);
    }

    delete(key = null) {
        let _key = this.toKey(key, false);
        return this.has(_key) ?
            Promise.resolve(Map.prototype.delete.call(this, _key))
            :
            Promise.reject();
    }

    toArray(entries = false) {
        return Array.from(entries ? this.entries() : this.values());
    }

    static GenerateID(prefix = 'toast-') {
        return `${prefix}${Date.now().toString(36)}+${(Math.random() * 50e6).toString(36)}`;
    }

    GenerateID(...args) {
        return this.constructor.GenerateID.apply(this, args);
    }

    clear() {
        return Map.prototype.clear.call(this) || this;
    }

    get [Symbol.toStringTag]() {
        return (this.constructor.name || 'NotificationStore');
    }
}

export {
    NotificationToasts,
    NotificationFilter,
    NotificationStore,
    ToastObject,
    Toast,
}
